import type { AdSize, ResponsiveAdSizeRule } from './types'

/**
 * Stable mioSegno inventory names used by screen-level ad placements.
 *
 * These names contain only application/surface metadata. Never add member,
 * song, share, file, e-mail, IP, device or other personal/content identifiers.
 */
export type MioSegnoAdPlacement =
  | 'owner-authoring'
  | 'owner-vault'
  | 'owner-segno-list'
  | 'shared-practice-segno-list'

export interface MioSegnoAdPlacementDefinition {
  adUnitId: string
  surface: string
  position: string
}

export const MIOSEGNO_DISPLAY_SIZES: readonly AdSize[] = [
  [970, 90],
  [728, 90],
  [468, 60],
  [320, 100],
  [320, 50],
]

export const MIOSEGNO_RESPONSIVE_SIZE_MAPPING: readonly ResponsiveAdSizeRule[] = [
  { viewport: [1024, 0], sizes: [[970, 90], [728, 90]] },
  { viewport: [768, 0], sizes: [[728, 90], [468, 60]] },
  { viewport: [0, 0], sizes: [[320, 100], [320, 50]] },
]

/**
 * Compact banner profile for the phone-landscape practice control bar.
 *
 * The bottom bar should be sized around the creative, not the other way
 * around. 90/100px-high leaderboards are intentionally excluded here so the
 * score/video keeps as much vertical space as possible.
 */
export const MIOSEGNO_LANDSCAPE_COMPACT_BANNER_SIZES: readonly AdSize[] = [
  [468, 60],
  [320, 50],
  [300, 50],
]

export const MIOSEGNO_LANDSCAPE_COMPACT_BANNER_MAPPING: readonly ResponsiveAdSizeRule[] = [
  { viewport: [1060, 0], sizes: [[468, 60], [320, 50]] },
  { viewport: [740, 0], sizes: [[320, 50], [300, 50]] },
  { viewport: [0, 0], sizes: [[300, 50]] },
]

const PLACEMENTS: Record<MioSegnoAdPlacement, MioSegnoAdPlacementDefinition> = {
  'owner-authoring': {
    adUnitId: '/2n4u/mioSegno/authoring-bottom',
    surface: 'authoring',
    position: 'bottom',
  },
  'owner-vault': {
    adUnitId: '/2n4u/mioSegno/library-bottom',
    surface: 'library',
    position: 'bottom',
  },
  'owner-segno-list': {
    adUnitId: '/2n4u/mioSegno/practice-bottom',
    surface: 'practice',
    position: 'segno-list',
  },
  'shared-practice-segno-list': {
    adUnitId: '/2n4u/mioSegno/shared-practice-bottom',
    surface: 'shared-practice',
    position: 'segno-list',
  },
}

export function mioSegnoAdPlacementDefinition(
  placement: MioSegnoAdPlacement,
): MioSegnoAdPlacementDefinition {
  return PLACEMENTS[placement]
}
